import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { AppBar } from '@/components/AppBar';
import { PageContainer, PageInner } from '@/components/ui/PageContainer';
import { InlineAlert } from '@/components/ui/InlineAlert';
import { MergeProgressDialog } from '@/components/MergeProgressDialog';
import { signInEmail, signInGoogle } from '@/lib/auth';
import { formatUserError } from '@/lib/errors';
import { listLocalQuizStates, type LocalQuizEntry } from '@/lib/quizStorage';
import { migrateLocalProgressTo, type LocalProgressInput } from '@/lib/userProgress';
import { createAttempt } from '@/lib/attempts';
import { getSectionDetailBySlug } from '@/lib/queries';
import { t } from '@/i18n/vi';

export const Route = createFileRoute('/login')({
  component: LoginScreen,
});

interface PendingMerge {
  uid: string;
  entries: LocalQuizEntry[];
}

function toLocalInputs(entries: LocalQuizEntry[]): LocalProgressInput[] {
  return entries.map((e) => ({
    courseSlug: e.courseSlug,
    sectionSlug: e.sectionSlug,
    picks: e.state.picks,
    crossed: e.state.crossed,
    currentTime: e.state.currentTime,
    status: e.state.status,
    showLiveScript: e.state.showLiveScript,
    updatedAtMs: e.state.updatedAt,
  }));
}

function LoginScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pendingMerge, setPendingMerge] = useState<PendingMerge | null>(null);
  const [mergeBusy, setMergeBusy] = useState(false);

  const afterSignIn = (uid: string) => {
    const entries = listLocalQuizStates().filter(
      ({ state }) => Object.keys(state.picks).length > 0 || state.currentTime > 5,
    );
    if (entries.length === 0) {
      navigate({ to: '/' });
      return;
    }
    setPendingMerge({ uid, entries });
  };

  const handleEmail = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const cred = await signInEmail(email.trim(), password);
      afterSignIn(cred.user.uid);
    } catch (e) {
      setErr(formatUserError(e).detail);
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setErr(null);
    setBusy(true);
    try {
      const result = await signInGoogle();
      afterSignIn(result.user.uid);
    } catch (e) {
      setErr(formatUserError(e).detail);
    } finally {
      setBusy(false);
    }
  };

  const handleMergeConfirm = async () => {
    if (!pendingMerge) return;
    setMergeBusy(true);
    try {
      await migrateLocalProgressTo(
        pendingMerge.uid,
        toLocalInputs(pendingMerge.entries),
        async (courseSlug, sectionSlug) => {
          const detail = await getSectionDetailBySlug(courseSlug, sectionSlug);
          return detail
            ? detail.questions.map((q) => ({ id: q.id, correct_option_order: q.correct_option_order }))
            : null;
        },
        createAttempt,
      );
    } catch {
      // partial failure: user can re-login and retry later
    } finally {
      setMergeBusy(false);
      setPendingMerge(null);
      navigate({ to: '/' });
    }
  };

  const handleMergeDismiss = () => {
    setPendingMerge(null);
    navigate({ to: '/' });
  };

  return (
    <PageContainer>
      <AppBar title={t.authSignIn} onBack={() => navigate({ to: '/' })} right={<span />} />
      <PageInner className="pt-6">
        <form onSubmit={handleEmail} className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-medium text-ink-2">{t.authEmail}</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-xl border border-border bg-surface px-3 py-2.5 text-[14px] text-ink-1"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-medium text-ink-2">{t.authPassword}</span>
            <input
              type="password"
              required
              minLength={6}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-xl border border-border bg-surface px-3 py-2.5 text-[14px] text-ink-1"
            />
          </label>

          {err && <InlineAlert>{err}</InlineAlert>}

          <button
            type="submit"
            disabled={busy}
            className="rounded-xl bg-[var(--brand)] py-2.5 text-[14px] font-semibold text-white disabled:opacity-60"
          >
            {busy ? t.authLoading : t.authSignIn}
          </button>
        </form>

        <div className="my-4 flex items-center gap-2 text-[12px] text-ink-2">
          <div className="h-px flex-1 bg-border" />
          <span>hoặc</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <button
          type="button"
          onClick={handleGoogle}
          disabled={busy}
          className="w-full rounded-xl border border-border bg-surface py-2.5 text-[14px] font-medium text-ink-1 disabled:opacity-60"
        >
          {t.authGoogle}
        </button>

        <div className="mt-5 flex flex-col items-center gap-2 text-[13px] text-ink-2">
          <Link to="/reset" className="text-[var(--brand)] hover:underline">
            {t.authForgot}
          </Link>
          <div>
            {t.authNoAccount}{' '}
            <Link to="/signup" className="font-semibold text-[var(--brand)] hover:underline">
              {t.authSignUp}
            </Link>
          </div>
        </div>
      </PageInner>

      {pendingMerge && (
        <MergeProgressDialog
          count={pendingMerge.entries.length}
          busy={mergeBusy}
          onConfirm={handleMergeConfirm}
          onDismiss={handleMergeDismiss}
        />
      )}
    </PageContainer>
  );
}
